import {
  getBrowserAuthHeaders,
  notifyBrowserAuthUnauthorized,
} from './browserAuthTransport';
import type {
  SavedPrompt,
  SavedPromptCreateRequest,
  SavedPromptDraft,
  SavedPromptFromChatRequest,
  SavedPromptOrderUpdateRequest,
  SavedPromptUpdateRequest,
  TourCompletionRequest,
  TourProgress,
  TourProgressListResponse,
  UserPreferences,
  UserPreferencesUpdate,
} from '../types/preferences';
import { API_BASE_URL } from '../utils/config';

const PREFERENCES_PATH = `${API_BASE_URL}/preferences`;
const SAVED_PROMPTS_PATH = `${PREFERENCES_PATH}/saved-prompts`;
const SAVED_PROMPT_DRAFT_FROM_CHAT_PATH = `${SAVED_PROMPTS_PATH}/draft-from-chat`;
const TOURS_PATH = `${PREFERENCES_PATH}/tours`;

const withSessionId = (path: string, sessionId: string): string => (
  `${path}?session_id=${encodeURIComponent(sessionId)}`
);

type PreferencesRequestBody = (
  UserPreferencesUpdate
  | TourCompletionRequest
  | SavedPromptCreateRequest
  | SavedPromptFromChatRequest
  | SavedPromptOrderUpdateRequest
  | SavedPromptUpdateRequest
);

const readErrorMessage = async (response: Response, fallbackMessage: string): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.detail === 'string' ? body.detail : fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const requestPreferences = async <T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: PreferencesRequestBody,
): Promise<T> => {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...getBrowserAuthHeaders(method),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401) {
    notifyBrowserAuthUnauthorized();
  }

  if (!response.ok) {
    const fallbackMessage = method === 'GET'
      ? 'Unable to load preferences.'
      : 'Unable to save preferences.';
    throw new Error(await readErrorMessage(response, fallbackMessage));
  }

  return response.json() as Promise<T>;
};

export const preferencesService = {
  getPreferences: (sessionId: string): Promise<UserPreferences> => (
    requestPreferences<UserPreferences>(withSessionId(PREFERENCES_PATH, sessionId), 'GET')
  ),
  updatePreferences: (sessionId: string, updates: UserPreferencesUpdate): Promise<UserPreferences> => (
    requestPreferences<UserPreferences>(withSessionId(PREFERENCES_PATH, sessionId), 'PATCH', updates)
  ),
  listTourProgress: (sessionId: string): Promise<TourProgressListResponse> => (
    requestPreferences<TourProgressListResponse>(withSessionId(TOURS_PATH, sessionId), 'GET')
  ),
  completeTour: (
    sessionId: string,
    tourId: string,
    completedVersion: number,
  ): Promise<TourProgress> => (
    requestPreferences<TourProgress>(
      withSessionId(`${TOURS_PATH}/${encodeURIComponent(tourId)}/completion`, sessionId),
      'PUT',
      { completed_version: completedVersion },
    )
  ),
  listSavedPrompts: (sessionId: string): Promise<SavedPrompt[]> => (
    requestPreferences<SavedPrompt[]>(withSessionId(SAVED_PROMPTS_PATH, sessionId), 'GET')
  ),
  createSavedPrompt: (sessionId: string, request: SavedPromptCreateRequest): Promise<SavedPrompt> => (
    requestPreferences<SavedPrompt>(withSessionId(SAVED_PROMPTS_PATH, sessionId), 'POST', request)
  ),
  generateSavedPromptDraft: (
    sessionId: string,
    sourcePrompt: SavedPromptFromChatRequest,
  ): Promise<SavedPromptDraft> => (
    requestPreferences<SavedPromptDraft>(
      withSessionId(SAVED_PROMPT_DRAFT_FROM_CHAT_PATH, sessionId),
      'POST',
      sourcePrompt,
    )
  ),
  updateSavedPrompt: (
    sessionId: string,
    promptId: string,
    request: SavedPromptUpdateRequest,
  ): Promise<SavedPrompt> => (
    requestPreferences<SavedPrompt>(
      withSessionId(`${SAVED_PROMPTS_PATH}/${encodeURIComponent(promptId)}`, sessionId),
      'PATCH',
      request,
    )
  ),
  updateSavedPromptOrder: (sessionId: string, promptIds: string[]): Promise<SavedPrompt[]> => (
    requestPreferences<SavedPrompt[]>(
      withSessionId(`${SAVED_PROMPTS_PATH}/order`, sessionId),
      'PATCH',
      { prompt_ids: promptIds },
    )
  ),
  deleteSavedPrompt: (sessionId: string, promptId: string): Promise<SavedPrompt> => (
    requestPreferences<SavedPrompt>(
      withSessionId(`${SAVED_PROMPTS_PATH}/${encodeURIComponent(promptId)}`, sessionId),
      'DELETE',
    )
  ),
};